import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCirclePlus } from '@fortawesome/free-solid-svg-icons'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import TimeOffForm, {
  GoogleCalendarConsentModal,
} from '../../components/TimeOffForm/TimeOffForm'
import { useGetAllUsersQuery } from '../../redux/user.endpoints'
import { User } from '../../types/User'
import { useLocation, useNavigate } from 'react-router'
import {
  useCreateTimeOffMutation,
  useLazyGetGoogleCalendarOAuthStartQuery,
} from '../../redux/timeoff.endpoints'
import { timeoffApi } from '../../redux/timeoff.api'
import { TimeOffDto } from '../../types/TimeOff'
import { toast } from 'react-toastify'
import moment from 'moment'
import { authContext } from '../../components/CognitoContextProvider/CognitoContextProvider'
import { useDispatch } from 'react-redux'

interface Props {
  user: User
}

const AddTimeOffs = ({ user }: Props) => {
  const [timeOffs, setTimeOffs] = useState<any[]>([])
  const [startDate, setStartDate] = useState<Date>(new Date())
  const [endDate, setEndDate] = useState<Date>(new Date())
  const { auth } = useContext(authContext)
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false)
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const idToken = useMemo(() => {
    console.log('auth', auth.data?.signInUserSession)
    if (!auth.data?.signInUserSession?.idToken?.jwtToken) return ''
    return auth.data.signInUserSession.idToken.jwtToken
  }, [auth.data])
  const location = useLocation()
  const previousPage = location.state?.from || '/time-off'

  const { data: usersData, refetch } = useGetAllUsersQuery({
    companyId: user.company.id,
    pageSize: 0,
    pageNumber: 0,
    sortBy: 'firstName',
    searchQuery: '',
    sortType: 'ASC',
    isActive: true,
  })
  const [users, setUsers] = useState<User[]>([])
  const [createTimeOff, createTimeOffResult] = useCreateTimeOffMutation()
  const [getGoogleCalendarOAuthStart] =
    useLazyGetGoogleCalendarOAuthStartQuery()

  useEffect(() => {
    refetch()
  }, [])

  useEffect(() => {
    if (usersData) setUsers(usersData)
  }, [usersData])

  const addNewTimeOffCard = () => {
    setTimeOffs(oldTimeoffs => [
      ...oldTimeoffs,
      {
        index: timeOffs.length + 1,
        name: '',
        description: '',
        approved: false,
        userId: user.id,
        startDate: null,
        endDate: null,
      },
    ])
  }

  const handleOnChange = (
    index: number,
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const changedTimeOff = timeOffs
    changedTimeOff[index][event.target.name] = event.target.value
    setTimeOffs(changedTimeOff)
  }

  const handleDateOnChange = (index: number, fieldName: string, date: Date) => {
    if (fieldName === 'startDate') setStartDate(date)
    else setEndDate(date)

    const changedTimeOff = timeOffs
    changedTimeOff[index][fieldName] = date
    setTimeOffs(changedTimeOff)
  }

  const handleUserSelectOnChange = (index: number, value: number) => {
    const changedTimeOff = timeOffs
    changedTimeOff[index]['userId'] = value
    setTimeOffs(changedTimeOff)
  }

  const removeTimeOff = (index: number) => {
    let changedTimeOff = timeOffs
    const valueToRemove = [changedTimeOff[index]]
    changedTimeOff = changedTimeOff.filter(
      element => !valueToRemove.includes(element),
    )
    setTimeOffs(changedTimeOff)
  }

  const getDisconnectedEmployees = () => {
    const uniqueUserIds = Array.from(
      new Set(
        timeOffs
          .map(item => parseInt(String(item.userId), 10))
          .filter(userId => !Number.isNaN(userId) && userId > 0),
      ),
    )

    return uniqueUserIds
      .map(userId => {
        if (userId === user.id) {
          return user.hasGoogleCalendarConnected ? null : user
        }

        const employee = users.find(u => u.id === userId)
        if (!employee || employee.hasGoogleCalendarConnected) {
          return null
        }

        return employee
      })
      .filter((employee): employee is User => Boolean(employee))
  }

  const getDisconnectedOtherEmployees = () =>
    getDisconnectedEmployees().filter(employee => employee.id !== user.id)

  const loggedInUserNeedsGoogleConnection = () =>
    timeOffs.some(item => parseInt(String(item.userId), 10) === user.id) &&
    !user.hasGoogleCalendarConnected

  const submitCreateTimeOff = (options?: { skipCalendarCheck?: boolean }) => {
    const array: TimeOffDto[] = []

    if (
      timeOffs.filter(
        item =>
          parseInt(item.userId) === 0 ||
          item.startDate === null ||
          item.endDate === null,
      ).length > 0
    ) {
      toast.error(
        'Time Offs are not completed, please check if there is an empty field.',
        {
          toastId: 'create-timeoff-error',
        },
      )
      return
    }

    let isOverlapping = false

    timeOffs.forEach(element => {
      const e1start = new Date(element.startDate).getTime()
      const e1end = new Date(element.endDate).getTime()

      timeOffs.forEach(timeoff => {
        if (
          timeoff.index !== element.index &&
          timeoff.userId === element.userId
        ) {
          const e2start = timeoff.startDate.getTime()
          const e2end = timeoff.endDate.getTime()

          if (
            (e1start >= e2start && e1start <= e2end) ||
            (e2start >= e1start && e2start <= e1end)
          ) {
            toast.warning(
              `Two Time Offs are overlapping, ${element.startDate.toLocaleDateString()}-${element.endDate.toLocaleDateString()} 
                  and ${timeoff.startDate.toLocaleDateString()}-${timeoff.endDate.toLocaleDateString()}`,
              {
                toastId: 'create-timeoff-warning',
              },
            )
            isOverlapping = true
          }
        }
      })
    })

    if (
      timeOffs.filter(item => moment(item.startDate) > moment(item.endDate))
        .length > 0
    ) {
      toast.error('The date ranges are incorrect, please check them.', {
        toastId: 'create-timeoff-error',
      })
      return
    }

    if (isOverlapping) {
      return
    }

    const disconnectedEmployees = getDisconnectedOtherEmployees()
    if (!options?.skipCalendarCheck && disconnectedEmployees.length > 0) {
      const names = disconnectedEmployees
        .map(employee => `${employee.firstName} ${employee.lastName}`)
        .join(', ')

      toast.error(
        `The following employees have not connected Google Calendar: ${names}. Time Off cannot be created.`,
        {
          toastId: 'google-calendar-not-connected',
        },
      )
      return
    }

    timeOffs.forEach(object => {
      array.push({
        name: object.name,
        description: object.description,
        approved: object.approved,
        userId: object.userId,
        startDate: moment(object.startDate).format('YYYY-MM-DD'),
        endDate: moment(object.endDate).format('YYYY-MM-DD'),
      })
    })

    try {
      createTimeOff({
        userIdWhoCreatedTimeoff: user.id,
        timeoffs: array,
      })
    } catch (err) {
      toast.error('Something went wrong', {
        toastId: 'create-timeoff-error',
      })
    }
  }

  const waitForGoogleOAuthResult = (popup: Window | null) =>
    new Promise<{
      success: boolean
      message?: string
    }>(resolve => {
      const onMessage = (event: MessageEvent) => {
        const payload = event.data

        if (!payload || payload.source !== 'google-calendar-oauth') return

        cleanup()
        resolve({
          success: Boolean(payload.success),
          message: payload.message,
        })
      }

      const cleanup = () => {
        clearInterval(interval)
        clearTimeout(timeout)
        window.removeEventListener('message', onMessage)
      }

      const interval = window.setInterval(() => {
        if (popup && popup.closed) {
          cleanup()
          resolve({
            success: false,
            message: 'Google login window was closed before completing auth.',
          })
        }
      }, 500)

      const timeout = window.setTimeout(() => {
        cleanup()
        resolve({
          success: false,
          message: 'Google login timed out. Please try again.',
        })
      }, 120000)

      window.addEventListener('message', onMessage)
    })

  const submitWithGoogleLogin = async () => {
    if (!idToken) {
      toast.error('Unable to start Google Calendar integration.', {
        toastId: 'google-oauth-start-error',
      })
      return
    }

    setIsConnectingGoogle(true)

    try {
      const oauthStart = await getGoogleCalendarOAuthStart(idToken).unwrap()
      const popup = window.open(
        oauthStart.authorizationUrl,
        'google-calendar-oauth',
        'width=600,height=700',
      )

      if (!popup) {
        toast.error(
          'Unable to open Google login window. Check popup permissions.',
          {
            toastId: 'google-popup-error',
          },
        )
        return
      }

      const oauthResult = await waitForGoogleOAuthResult(popup)

      if (oauthResult.success) {
        dispatch(timeoffApi.util.invalidateTags(['LoggedInUser']))
        toast.success('Google Calendar connected successfully.', {
          toastId: 'google-connected-success',
        })
        setIsGoogleModalOpen(false)
        submitCreateTimeOff({ skipCalendarCheck: true })
        return
      }

      toast.warning(
        oauthResult.message || 'Google login did not finish. Please try again.',
        {
          toastId: 'google-connected-warning',
        },
      )
    } catch (error) {
      toast.error('Unable to start Google Calendar integration.', {
        toastId: 'google-oauth-start-error',
      })
    } finally {
      setIsConnectingGoogle(false)
    }
  }

  const handleSaveClick = () => {
    const disconnectedOthers = getDisconnectedOtherEmployees()

    if (disconnectedOthers.length > 0) {
      const names = disconnectedOthers
        .map(employee => `${employee.firstName} ${employee.lastName}`)
        .join(', ')

      toast.error(
        `The following employees have not connected Google Calendar: ${names}. Time Off cannot be created.`,
        {
          toastId: 'google-calendar-not-connected',
        },
      )
      return
    }

    if (loggedInUserNeedsGoogleConnection()) {
      setIsGoogleModalOpen(true)
      return
    }

    submitCreateTimeOff()
  }

  useEffect(() => {
    if (createTimeOffResult.isError) {
      toast.error(
        'Server Error, something happen when trying to create Time Offs',
        {
          toastId: 'create-timeoff-error',
        },
      )
    } else if (timeOffs.length > 0) {
      if (createTimeOffResult.data?.filter(item => item.status).length === 0) {
        setTimeOffs([])
        toast.success('Time Offs created succesfully', {
          toastId: 'create-timeoff-success',
        })
        navigate(previousPage)
      } else {
        createTimeOffResult.data?.forEach((element, index) => {
          if (element.response) {
            toast.warning(element.response.error, {
              toastId: 'create-timeoff-warning' + index,
            })
          }
        })
        removeTimes(createTimeOffResult.data || [])
      }
    }
  }, [createTimeOffResult])

  const removeTimes = (responses: any[]) => {
    const indexes: number[] = []

    if (responses) {
      responses.forEach(element => {
        if (!element.status) indexes.push(responses.indexOf(element))
      })
    }

    const newTimeOffs = timeOffs.filter(
      element =>
        indexes.filter(value => value === element.index - 1).length === 0,
    )

    setTimeOffs(newTimeOffs)
  }

  return (
    <div className="container">
      <div className="my-5">
        <h1>Add New Time Off</h1>
        <p>
          (<span className="text-danger">*</span>)<strong>Required</strong>
        </p>
      </div>
      <div className="timeoffs d-flex">
        {timeOffs.map((timeOff, index) => {
          return (
            <div key={index} className="timeoff-card me-2">
              <TimeOffForm
                index={index}
                users={user.isAdmin ? users : [user]}
                handleOnChange={handleOnChange}
                handleDateOnChange={handleDateOnChange}
                timeOffs={timeOffs}
                startDate={startDate}
                endDate={endDate}
                removeTimeOff={removeTimeOff}
                handleUserSelectOnChange={handleUserSelectOnChange}
                currentUser={user}
              />
            </div>
          )
        })}
        <div className="card-button d-flex flex-column">
          <div className="m-auto text-center">
            <div className="add-button-circle">
              <FontAwesomeIcon
                onClick={() => addNewTimeOffCard()}
                size="2x"
                className="circle-plus-button pointer-cursor"
                icon={faCirclePlus}
              />
            </div>
            <div className="card-button-text">
              <span>Add New Time Off</span>
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-12 text-center mt-4">
        <button
          onClick={() => navigate(previousPage)}
          className="btn btn-secondary me-2"
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSaveClick}
          disabled={timeOffs.length == 0}
        >
          Save
        </button>
      </div>

      <GoogleCalendarConsentModal
        show={isGoogleModalOpen}
        isConnecting={isConnectingGoogle}
        onClose={() => setIsGoogleModalOpen(false)}
        onConnect={() => submitWithGoogleLogin()}
      />
    </div>
  )
}

export default AddTimeOffs
